import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth, hasRole } from "@/context/AuthContext";
import { viewFor } from "@/config/roleViews";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useLiveRefresh } from "@/lib/live";
import { EmptyRow, Kpi, ORANGE_BTN, Panel, Pill, RollsLink, SubTabs, money, qty, unit } from "@/components/stores/storesUi";
import { ExportButton, ImportButton } from "@/components/stores/ImportExport";
import { ItemDialog } from "@/components/stores/StoreDialogs";

export default function StockTab({ initialView = "report", initialStatus = "all", initialQ = "" }) {
  const { user } = useAuth();
  const canManage = hasRole(user, "production_manager", "accounts");
  const isAdmin = user?.role === "admin";
  const [view, setView] = useState(initialView);
  const [editing, setEditing] = useState(null); // null | "new" | item_code
  // Saves and imports announce a stores change, which reloads the open report (lib/live.js).

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SubTabs
          value={view}
          onChange={setView}
          testid="stock-view"
          options={[
            { value: "report", label: "Stock report" },
            { value: "aging", label: "Aging" },
            { value: "slow", label: "Slow moving" },
          ]}
        />
        {canManage && (
          <div className="flex flex-wrap gap-2">
            {isAdmin && <ImportButton />}
            <ExportButton />
            <Button data-testid="stock-new-item" className={ORANGE_BTN} onClick={() => setEditing("new")}><Plus size={13} /> New item</Button>
          </div>
        )}
      </div>
      {view === "report" && <StockReport initialStatus={initialStatus} initialQ={initialQ} onEdit={canManage ? setEditing : null} />}
      {view === "aging" && <Aging />}
      {view === "slow" && <SlowMoving />}
      {editing && (
        <ItemDialog
          itemCode={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function StockReport({ initialStatus, initialQ, onEdit }) {
  const { user } = useAuth();
  const showMoney = viewFor(user).finance;
  const [data, setData] = useState(null);
  const [q, setQ] = useState(initialQ);
  const [status, setStatus] = useState(initialStatus);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = (quiet = false) => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (status !== "all") params.status = status;
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    return api.get("/stores/stock-report", { params }).then(({ data }) => setData(data)).catch((e) => !quiet && toast.error(formatApiError(e)));
  };
  useEffect(() => { load(); }, [status, from, to]); // eslint-disable-line react-hooks/exhaustive-deps
  useLiveRefresh("stores", () => load(true));

  const rows = data?.rows || [];
  const cols = showMoney ? 16 : 15;

  return (
    <div className="space-y-4">
      <div className="border border-neutral-200 bg-white p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-56">
          <label className="label-caps block mb-1">Search</label>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-neutral-400" />
            <Input data-testid="stock-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
              className="pl-8 rounded-sm" placeholder="Item code, material, category, supplier…" />
          </div>
        </div>
        <div>
          <label className="label-caps block mb-1">Status</label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="stock-status" className="rounded-sm w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All items</SelectItem>
              <SelectItem value="ORDER">Reorder</SelectItem>
              <SelectItem value="OK">OK</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><label className="label-caps block mb-1">From</label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-sm w-40" /></div>
        <div><label className="label-caps block mb-1">To</label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-sm w-40" /></div>
        <Button variant="outline" className="rounded-sm" onClick={() => load()}>Apply</Button>
      </div>

      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi label="Items" value={qty(rows.length, 0)} />
          <Kpi label="Closing stock (m + kg)" value={qty(data.totals.closing, 0)}
            sub={data.totals.awaiting_qc ? `${qty(data.totals.usable, 0)} usable · ${qty(data.totals.awaiting_qc, 0)} awaiting QC` : null} />
          <Kpi label="Closing stock (SQM)" value={qty(data.totals.closing_sqm, 0)} />
          {showMoney && data.totals.value != null
            ? <Kpi label="Stock value" value={money(data.totals.value)} />
            : <Kpi label="Reorder required" value={qty(data.totals.order_items, 0)} tone={data.totals.order_items ? "danger" : "ok"} />}
        </div>
      )}

      <Panel>
        <div className="overflow-x-auto max-h-[70vh]">
          <table className="w-full text-sm [&_td]:whitespace-nowrap [&_th]:whitespace-nowrap" data-testid="stock-table">
            <thead className="bg-neutral-50 label-caps text-[10px] sticky top-0 z-10">
              <tr>
                <th className="text-left px-3 py-2.5">Item</th>
                <th className="text-right px-3 py-2.5">Width</th>
                <th className="text-right px-3 py-2.5">Opening</th>
                <th className="text-right px-3 py-2.5">Purchase</th>
                <th className="text-right px-3 py-2.5">Return</th>
                <th className="text-right px-3 py-2.5">Slit in</th>
                <th className="text-right px-3 py-2.5">Issue</th>
                <th className="text-right px-3 py-2.5">Slit out</th>
                <th className="text-right px-3 py-2.5">Closing</th>
                <th className="text-right px-3 py-2.5" title="Received, not yet released by QC: cannot be issued">Awaiting QC</th>
                <th className="text-right px-3 py-2.5" title="Closing less what QC holds or rejected">Usable</th>
                <th className="text-right px-3 py-2.5">SQM</th>
                <th className="text-right px-3 py-2.5">Min / Max</th>
                <th className="text-left px-3 py-2.5">Status</th>
                {showMoney && <th className="text-right px-3 py-2.5">Value</th>}
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <EmptyRow cols={cols} loading={!data}>No items match.</EmptyRow>}
              {rows.map((r) => (
                <tr key={r.item_code} className="border-t border-neutral-100 data-row" data-testid={`stock-row-${r.item_code}`}>
                  <td className="px-3 py-2 min-w-[14rem]">
                    <RollsLink q={r.item_code} className="font-mono-num text-xs font-semibold" />
                    <div className="text-xs text-neutral-500 truncate max-w-xs">{r.material_name}{r.category ? ` · ${r.category}` : ""}</div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono-num">{qty(r.width)}</td>
                  <Num v={r.opening} />
                  <Num v={r.purchase} />
                  <Num v={r.production_return} />
                  <Num v={r.slit_in} />
                  <Num v={r.issue} />
                  <Num v={r.slit_out} />
                  <td className="px-3 py-2 text-right font-mono-num font-semibold whitespace-nowrap">
                    {qty(r.closing)} <span className="text-[10px] text-neutral-500 font-normal">{unit(r.uom)}</span>
                  </td>
                  <td className={`px-3 py-2 text-right font-mono-num ${r.awaiting_qc ? "text-amber-700" : "text-neutral-300"}`}>{r.awaiting_qc ? qty(r.awaiting_qc) : "—"}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{qty(r.usable)}</td>
                  <Num v={r.closing_sqm} />
                  <td className="px-3 py-2 text-right font-mono-num text-xs text-neutral-500 whitespace-nowrap">{qty(r.min_stock, 0)} / {qty(r.max_stock, 0)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <Pill value={r.status} />
                    {r.status === "ORDER" && <span className="ml-1.5 text-[11px] text-rose-700 font-mono-num">+{qty(r.order_qty, 0)}</span>}
                  </td>
                  {showMoney && <td className="px-3 py-2 text-right font-mono-num">{money(r.value)}</td>}
                  <td className="px-2 py-2">
                    {onEdit && (
                      <button title="Edit item" onClick={() => onEdit(r.item_code)} className="text-neutral-400 hover:text-[#FF5A00]">
                        <Pencil size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

function Num({ v }) {
  return <td className={`px-3 py-2 text-right font-mono-num ${v ? "" : "text-neutral-300"}`}>{v ? qty(v) : "—"}</td>;
}

function Aging() {
  const { user } = useAuth();
  const showMoney = viewFor(user).finance;
  const [data, setData] = useState(null);
  const [bucket, setBucket] = useState("all");

  const load = (quiet = false) =>
    api.get("/stores/aging").then(({ data }) => setData(data)).catch((e) => !quiet && toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useLiveRefresh("stores", () => load(true));

  const rolls = useMemo(
    () => (data?.rolls || []).filter((r) => bucket === "all" || r.aging_category === bucket),
    [data, bucket],
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {(data?.summary || []).map((b) => (
          <button key={b.bucket} type="button" onClick={() => setBucket(bucket === b.bucket ? "all" : b.bucket)}
            className={`text-left ${bucket === b.bucket ? "ring-2 ring-[#FF5A00]" : ""}`}>
            <Kpi label={b.bucket} value={`${qty(b.rolls, 0)} rolls`}
              sub={`${qty(b.sqm, 0)} SQM${showMoney && b.value != null ? ` · ${money(b.value)}` : ""}`}
              tone={b.bucket === "90+ Days" && b.rolls ? "danger" : b.bucket === "61-90 Days" && b.rolls ? "warn" : undefined} />
          </button>
        ))}
      </div>
      <Panel title={`Rolls in stock${bucket !== "all" ? ` · ${bucket}` : ""}`}>
        <div className="overflow-x-auto max-h-[60vh]">
          <table className="w-full text-sm [&_td]:whitespace-nowrap [&_th]:whitespace-nowrap">
            <thead className="bg-neutral-50 label-caps text-[10px] sticky top-0">
              <tr>
                <th className="text-left px-3 py-2.5">Roll</th>
                <th className="text-left px-3 py-2.5">Item</th>
                <th className="text-left px-3 py-2.5">GRN</th>
                <th className="text-left px-3 py-2.5">Received</th>
                <th className="text-right px-3 py-2.5">Age (days)</th>
                <th className="text-right px-3 py-2.5">Balance</th>
                <th className="text-right px-3 py-2.5">SQM</th>
                {showMoney && <th className="text-right px-3 py-2.5">Value</th>}
              </tr>
            </thead>
            <tbody>
              {rolls.length === 0 && <EmptyRow cols={showMoney ? 8 : 7} loading={!data}>No rolls.</EmptyRow>}
              {rolls.slice(0, 1000).map((r) => (
                <tr key={r.roll_no} className="border-t border-neutral-100 data-row">
                  <td className="px-3 py-2"><RollsLink q={r.roll_no} className="font-mono-num text-xs" /></td>
                  <td className="px-3 py-2">
                    <RollsLink q={r.item_code} className="font-mono-num text-xs" />
                    <div className="text-[11px] text-neutral-500 truncate max-w-[16rem]">{r.item_name}</div>
                  </td>
                  <td className="px-3 py-2 font-mono-num text-xs text-neutral-500">{r.grn_no || "Opening"}</td>
                  <td className="px-3 py-2 font-mono-num text-xs">{r.received_date}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{r.aging_days ?? "—"}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{qty(r.balance)} <span className="text-[10px] text-neutral-500">{unit(r.uom)}</span></td>
                  <td className="px-3 py-2 text-right font-mono-num">{qty(r.sqm)}</td>
                  {showMoney && <td className="px-3 py-2 text-right font-mono-num">{money(r.value)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

function SlowMoving() {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("NON MOVING");

  const load = (quiet = false) =>
    api.get("/stores/slow-moving").then(({ data }) => setRows(data)).catch((e) => !quiet && toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useLiveRefresh("stores", () => load(true));

  const counts = useMemo(() => (rows || []).reduce((c, r) => ({ ...c, [r.status]: (c[r.status] || 0) + 1 }), {}), [rows]);
  const shown = (rows || []).filter((r) => status === "all" || r.status === status);

  return (
    <div className="space-y-4">
      <SubTabs
        value={status}
        onChange={setStatus}
        testid="slow-status"
        options={[
          { value: "NON MOVING", label: `Non-moving · ${counts["NON MOVING"] || 0}` },
          { value: "SLOW MOVING", label: `Slow moving · ${counts["SLOW MOVING"] || 0}` },
          { value: "ACTIVE", label: `Active · ${counts.ACTIVE || 0}` },
          { value: "all", label: "All" },
        ]}
      />
      <Panel>
        <div className="overflow-x-auto max-h-[65vh]">
          <table className="w-full text-sm [&_td]:whitespace-nowrap [&_th]:whitespace-nowrap">
            <thead className="bg-neutral-50 label-caps text-[10px] sticky top-0">
              <tr>
                <th className="text-left px-3 py-2.5">Item</th>
                <th className="text-right px-3 py-2.5">Width</th>
                <th className="text-right px-3 py-2.5">Current stock</th>
                <th className="text-left px-3 py-2.5">Last issue</th>
                <th className="text-right px-3 py-2.5">Days since</th>
                <th className="text-left px-3 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && <EmptyRow cols={6} loading={!rows}>Nothing in this group.</EmptyRow>}
              {shown.map((r) => (
                <tr key={r.item_code} className="border-t border-neutral-100 data-row">
                  <td className="px-3 py-2">
                    <RollsLink q={r.item_code} className="font-mono-num text-xs font-semibold" />
                    <div className="text-xs text-neutral-500 truncate max-w-sm">{r.material_name}</div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono-num">{qty(r.width)}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{qty(r.current_stock)}</td>
                  <td className="px-3 py-2 font-mono-num text-xs">{r.last_issue_date || "Never issued"}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{r.days_since_last_issue ?? "—"}</td>
                  <td className="px-3 py-2"><Pill value={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
